var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as signalR from "@microsoft/signalr";
import localStorageAccessor from "@utility/local-storage-accessor";
import { config } from "../config/runtime-config";
export var NotificationType;
(function (NotificationType) {
    NotificationType[NotificationType["NodeAdded"] = 1] = "NodeAdded";
    NotificationType[NotificationType["AccessPointAdded"] = 2] = "AccessPointAdded";
    NotificationType[NotificationType["AccessPointDisconnected"] = 3] = "AccessPointDisconnected";
    NotificationType[NotificationType["AccessPointConnected"] = 4] = "AccessPointConnected";
    NotificationType[NotificationType["AlarmGenerated"] = 5] = "AlarmGenerated";
    NotificationType[NotificationType["NodeDisconnected"] = 6] = "NodeDisconnected";
    NotificationType[NotificationType["AccessPointTimestampUpdated"] = 7] = "AccessPointTimestampUpdated";
})(NotificationType || (NotificationType = {}));
class SignalRService {
    constructor() {
        this.connection = null;
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;
        this.reconnectDelay = 3000;
        this.isStarting = false;
        this.connectionPromise = null;
    }
    getApiBaseUrl() {
        const envUrl = config.apiBaseUrl;
        const fallbackUrl = "https://localhost:7247/api/";
        return envUrl || fallbackUrl;
    }
    getHubUrl() {
        const apiBaseUrl = this.getApiBaseUrl();
        const baseUrl = apiBaseUrl.replace(/\/api\/?$/, "");
        return `${baseUrl}/temperatureHub`;
    }
    startConnection() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            if (((_a = this.connection) === null || _a === void 0 ? void 0 : _a.state) === signalR.HubConnectionState.Connected) {
                return;
            }
            if (this.isStarting && this.connectionPromise) {
                return this.connectionPromise;
            }
            if (this.connection &&
                this.connection.state !== signalR.HubConnectionState.Disconnected) {
                try {
                    yield this.connection.stop();
                }
                catch (error) {
                    // Ignore errors when stopping
                }
                this.connection = null;
            }
            const token = localStorageAccessor.getAccessToken();
            if (!token) {
                console.warn("⚠️ No access token found, cannot connect to SignalR");
                return;
            }
            this.isStarting = true;
            const hubUrl = this.getHubUrl();
            this.connectionPromise = (() => __awaiter(this, void 0, void 0, function* () {
                var _a, _b, _c, _d;
                try {
                    this.connection = new signalR.HubConnectionBuilder()
                        .withUrl(hubUrl, {
                        accessTokenFactory: () => {
                            const currentToken = localStorageAccessor.getAccessToken();
                            if (!currentToken) {
                                console.warn("⚠️ No access token available for SignalR connection");
                            }
                            return currentToken || "";
                        },
                        skipNegotiation: false,
                        transport: signalR.HttpTransportType.LongPolling |
                            signalR.HttpTransportType.WebSockets,
                    })
                        .withAutomaticReconnect({
                        nextRetryDelayInMilliseconds: (retryContext) => {
                            if (retryContext.previousRetryCount < this.maxReconnectAttempts) {
                                return this.reconnectDelay;
                            }
                            return null;
                        },
                    })
                        .configureLogging(signalR.LogLevel.Warning)
                        .build();
                    this.connection.onclose((error) => {
                        this.reconnectAttempts = 0;
                        this.isStarting = false;
                    });
                    this.connection.onreconnecting((error) => {
                        this.reconnectAttempts++;
                    });
                    this.connection.onreconnected((connectionId) => {
                        this.reconnectAttempts = 0;
                        this.isStarting = false;
                    });
                    yield this.connection.start();
                    this.reconnectAttempts = 0;
                    this.isStarting = false;
                }
                catch (error) {
                    this.isStarting = false;
                    if (((_a = error === null || error === void 0 ? void 0 : error.message) === null || _a === void 0 ? void 0 : _a.includes("404")) ||
                        ((_b = error === null || error === void 0 ? void 0 : error.message) === null || _b === void 0 ? void 0 : _b.includes("Not Found"))) {
                        console.error("SignalR Hub not found. Check if API is running and Hub is mapped correctly.");
                    }
                    else if (!((_c = error === null || error === void 0 ? void 0 : error.message) === null || _c === void 0 ? void 0 : _c.includes("WebSocket")) &&
                        !((_d = error === null || error === void 0 ? void 0 : error.message) === null || _d === void 0 ? void 0 : _d.includes("transport"))) {
                        console.error("Error starting SignalR connection:", error);
                    }
                    throw error;
                }
                finally {
                    this.connectionPromise = null;
                }
            }))();
            return this.connectionPromise;
        });
    }
    stopConnection() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.connection) {
                try {
                    yield this.connection.stop();
                    this.connection = null;
                }
                catch (error) {
                    // Ignore errors when stopping
                    this.connection = null;
                }
            }
            this.isStarting = false;
            this.connectionPromise = null;
        });
    }
    onTemperatureUpdate(callback) {
        if (!this.connection) {
            console.warn("SignalR connection not established");
            return;
        }
        try {
            this.connection.on("TemperatureUpdate", (update) => {
                callback(update);
            });
        }
        catch (error) {
            console.error("Error subscribing to temperature updates:", error);
        }
    }
    offTemperatureUpdate() {
        if (this.connection) {
            try {
                this.connection.off("TemperatureUpdate");
            }
            catch (error) {
                // Ignore errors
            }
        }
    }
    onNotificationUpdate(callback) {
        if (!this.connection) {
            console.warn("SignalR connection not established");
            return;
        }
        try {
            this.connection.on("NotificationUpdate", (update) => {
                callback(update);
            });
        }
        catch (error) {
            console.error("❌ Error subscribing to notification updates:", error);
        }
    }
    offNotificationUpdate() {
        if (this.connection) {
            try {
                this.connection.off("NotificationUpdate");
            }
            catch (error) {
                // Ignore errors
            }
        }
    }
    getConnectionState() {
        var _a, _b;
        return (_b = (_a = this.connection) === null || _a === void 0 ? void 0 : _a.state) !== null && _b !== void 0 ? _b : null;
    }
    isConnected() {
        var _a;
        return ((_a = this.connection) === null || _a === void 0 ? void 0 : _a.state) === signalR.HubConnectionState.Connected;
    }
}
export const signalRService = new SignalRService();
